import { ReactNode, useMemo, useRef, useState } from "react"

import cloneDeep from "lodash-es/cloneDeep"

import { useAPIHealth } from "shared/api/health/useAPIHealth"
import {
  IMAGE_OVERLAY,
  INSTALLATION_REFERENCE_SELECTION_OVERLAY,
  PORTFOLIO_PRODUCT_SELECT_OVERLAY,
  PRODUCT_CONFIGURATION_OVERLAY
} from "shared/context/overlay/types"
import { Logger } from "shared/lib/Logger"
import { exists } from "shared/lib/exists"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import { ApiResponse } from "shared/types/ApiResponse"
import { FilePreview } from "shared/types/File"
import { ProductConfiguratorMode } from "shared/types/ProductConfiguratorMode"
import { ProductPosition } from "shared/types/ProductPosition"
import {
  ProductPositionTypename,
  SpecialPriceGroupTypename
} from "shared/types/Project"
import { FormRef } from "shared/types/ReactHookForm"
import { ProductPositionInstallationReference } from "shared/types/__generated__/graphql"

import { useAttachmentsQuery } from "entities/attachments/api/useAttachmentsQuery"
import { useProjectAttachmentUpload } from "entities/attachments/api/useProjectAttachmentUpload"
import { useUpdateAttachmentLinks } from "entities/attachments/api/useUpdateAttachmentLinks"
import { useInstallationReferenceObjTransformer } from "entities/building-references/model/useInstallationReferenceObjTransformer"
import { ClarificationRequestModel } from "entities/clarification-requests/types/ClarificationRequestModel"
import { useProductPositionInstallationReferenceObjs } from "entities/product-positions/model/useProductPositionInstallationReferenceObjs"
import { useProductPositionPrice } from "entities/product-positions/model/useProductPositionPrice"
import {
  ProductPositionFormData,
  ProductPositionFormState
} from "entities/product-positions/types/ProductPositionForm"
import { getProductInformationFiles } from "entities/products/lib/getProductInformationFiles"
import { getProductType } from "entities/products/lib/getProductType"
import { useExternalDimensioningModelAvailability } from "entities/products/model/useExternalDimensioningModelAvailability"
import { useProductConfigurationModelAvailability } from "entities/products/model/useProductConfigurationModelAvailability"
import { useVariantKey } from "entities/products/model/useVariantKey"
import { useCreateSpecialPriceGroup } from "entities/projects/api/useCreateSpecialPriceGroup"
import { useDeleteSpecialPriceGroup } from "entities/projects/api/useDeleteSpecialPriceGroup"
import { useProjectAttachmentsData } from "entities/projects/api/useProjectAttachmentsData"
import { useProjectData } from "entities/projects/api/useProjectData"
import { usePriceGroupOptions } from "entities/projects/model/usePriceGroupOptions"
import { useProjectOptions } from "entities/projects/model/useProjectOptions"
import { useProjectPermissions } from "entities/projects/model/useProjectPermissions"
import { useProjectPositionOptions } from "entities/projects/model/useProjectPositionOptions"
import { useRoomOptions } from "entities/rooms/model/useRoomOptions"
import { ProductPositionFormModel } from "features/product-position-management/types/ProductPositionFormModel"
import { ProductPositionFormOptions } from "features/product-position-management/types/ProductPositionFormOptions"
import { ProductConfiguratorResult } from "widgets/product-configurator/model/useProductConfiguratorModel"

import { useDeleteInstallationReference } from "./useDeleteInstallationReference"

export interface UseProductPositionFormModelArgs {
  id: string
  productPosition: Partial<ProductPosition> | null
  defaultProjectID: string
  excludedProjectIDs?: string[]
  defaultParentID: string
  displayPosition?: number
  isDefaultEditing: boolean
  isEditing?: boolean
  ensureEditableProject?: boolean

  isLoading?: boolean

  clarificationRequest?: ClarificationRequestModel | null

  formOptions?: ProductPositionFormOptions

  onConfigurationChange?: (configuration: ProductConfiguratorResult) => void
  onInstallationReferencesChange?: (
    productPositionInstallationReferences:
      | ProductPositionInstallationReference[]
      | null
  ) => void
  onSubmit?: (
    formData: ProductPositionFormData
  ) => Promise<ApiResponse<unknown>> | void
  onCancel?: () => void
}

export function useProductPositionFormModel(
  args: UseProductPositionFormModelArgs
): ProductPositionFormModel {
  const {
    productPosition,
    ensureEditableProject,
    excludedProjectIDs,
    defaultProjectID,
    onSubmit
  } = args
  const formRef: FormRef<
    ProductPositionFormState,
    unknown,
    ProductPositionFormData
  > = useRef(null)

  const [formData, setFormData] = useState<
    ProductPositionFormState | undefined
  >()

  const { openOverlay: openProductConfigurationModelSelectionOverlay } =
    useOverlay(PORTFOLIO_PRODUCT_SELECT_OVERLAY)
  const { openOverlay: openProductConfigurationOverlay } = useOverlay(
    PRODUCT_CONFIGURATION_OVERLAY
  )
  const { openOverlay: openInstallationReferenceSelectionOverlay } = useOverlay(
    INSTALLATION_REFERENCE_SELECTION_OVERLAY
  )

  const { price: isPriceServiceAvailable } = useAPIHealth()

  const { productType } = useVariantKey(
    formData?.productPosition?.variantKey || productPosition?.variantKey || null
  )

  const {
    isConfigurationAvailable: hasProductConfigurationModel,
    isLoading: isLoadingProductConfigurationModels,
    data: productConfigurationModelsData
  } = useProductConfigurationModelAvailability(productType)
  const { isExternalDimensioningAvailable: hasExternalDimensioningModel } =
    useExternalDimensioningModelAvailability(productType)

  const { options: projectOptions, isLoading: isLoadingProjectOptions } =
    useProjectOptions({ excludedProjectIDs })

  const effectiveDefaultProjectID = useMemo(() => {
    if (!ensureEditableProject || isLoadingProjectOptions) {
      return defaultProjectID
    }

    const isEditable = projectOptions.some(
      opt => opt.value === defaultProjectID
    )
    return isEditable
      ? defaultProjectID
      : projectOptions[0]?.value ?? defaultProjectID
  }, [
    ensureEditableProject,
    defaultProjectID,
    projectOptions,
    isLoadingProjectOptions
  ])

  const projectID = formData?.projectID || effectiveDefaultProjectID

  const { price, isLoading: isLoadingPrice } = useProductPositionPrice({
    id: productPosition?.id,
    quantity: productPosition?.quantity,
    projectID
  })

  const hideAttachments = args.formOptions?.hideAttachments
  const { attachments, attachmentCategories } = useProjectAttachmentsData({
    projectID,
    options: { skip: hideAttachments }
  })
  const { createAttachment } = useProjectAttachmentUpload()
  const updateAttachmentLinks = useUpdateAttachmentLinks()

  const handleUpload = useEvent((file: FilePreview, category: string) => {
    return createAttachment(file.file, { projectID, category })
  })

  const { data: linkedAttachmentsData } = useAttachmentsQuery({
    id: productPosition?.id ?? "",
    resourceType: ProductPositionTypename,
    skip: !productPosition?.id || hideAttachments
  })

  const linkedAttachments = linkedAttachmentsData?.attachments

  const {
    project,
    permissions,
    isLoading: isLoadingSpecialPriceGroups
  } = useProjectData(projectID, true)

  // useProjectData's isLoadingPermissions also covers the project query; this
  // narrower flag tracks only whether the permissions themselves are known.
  const { isLoading: isLoadingPermissions } = useProjectPermissions({
    projectID
  })

  const isSpecialPriceGroup = useMemo(() => {
    return project?.specialPriceGroups?.some(
      group => group.name === productType
    )
  }, [productType, project?.specialPriceGroups])

  const createSpecialPriceGroup = useCreateSpecialPriceGroup()
  const deleteSpecialPriceGroup = useDeleteSpecialPriceGroup()

  const { options: roomOptions, isLoading: isLoadingParentOptions } =
    useRoomOptions(projectID)

  const { positionOptions, isLoading: isLoadingPositionOptions } =
    useProjectPositionOptions({
      projectID,
      parentID: formData?.parentID,
      childID: productPosition?.id,
      childType: ProductPositionTypename
    })

  const { options: priceGroupOptions, isLoading: isLoadingPriceGroupOptions } =
    usePriceGroupOptions()

  const handleOpenProductConfiguration = useEvent(
    (params: {
      onConfigurationChange: (configuration: ProductConfiguratorResult) => void
      mode?: ProductConfiguratorMode
    }) => {
      const { onConfigurationChange, mode } = params
      const handleConfigurationChange = (
        configuration: ProductConfiguratorResult
      ) => {
        args.onConfigurationChange?.(configuration)
        onConfigurationChange(configuration)
      }

      if (formData?.productPosition?.variantKey) {
        openProductConfigurationOverlay({
          configuration: {
            variantKey: formData.productPosition.variantKey.toUpperCase(),
            configurationKey: formData.productPosition.configurationKey,
            externalDimensioningResult:
              mode === ProductConfiguratorMode.DIMENSIONING
                ? formData.productPosition.dimensioning
                : undefined
          },
          mode,
          onSubmit: handleConfigurationChange
        })
      } else {
        Logger.error(
          "could not open product configuration, no variant key available"
        )
      }
    }
  )

  const handleOpenPortfolio = useEvent(
    (
      onConfigurationChange: (configuration: ProductConfiguratorResult) => void
    ) => {
      const handleConfigurationChange = (
        configuration: ProductConfiguratorResult
      ) => {
        args.onConfigurationChange?.(configuration)
        onConfigurationChange(configuration)
      }

      openProductConfigurationModelSelectionOverlay({
        onSubmit: handleConfigurationChange
      })
    }
  )

  const handleProductConfiguration = useEvent(
    (
      onConfigurationChange: (configuration: ProductConfiguratorResult) => void
    ) => {
      handleOpenProductConfiguration({
        onConfigurationChange,
        mode: ProductConfiguratorMode.CONFIGURATION
      })
    }
  )

  const handleProductDimensioning = useEvent(
    (
      onConfigurationChange: (configuration: ProductConfiguratorResult) => void
    ) => {
      handleOpenProductConfiguration({
        onConfigurationChange,
        mode: ProductConfiguratorMode.DIMENSIONING
      })
    }
  )

  const installationReferenceIDs = useMemo(
    () =>
      formData?.productPosition?.references
        ?.map(reference => reference?.permalink)
        .filter((p): p is string => p != null),
    [formData?.productPosition?.references]
  )

  const { installationReferenceObjs } =
    useProductPositionInstallationReferenceObjs({
      installationReferenceIDs
    })

  const installationReferences = useInstallationReferenceObjTransformer({
    installationReferenceObjs
  })

  const isLoadingInstallationReferences =
    !!productPosition?.references &&
    productPosition?.references.length > 0 &&
    installationReferences === undefined

  const handleDeleteInstallationReference = useDeleteInstallationReference({
    references: productPosition?.references,
    onInstallationReferencesChange: args.onInstallationReferencesChange
  })

  const { openOverlay: openInstallationReferenceImageOverlay } =
    useOverlay(IMAGE_OVERLAY)

  const handleOpenInstallationReferenceImageOverlay = useEvent(
    (content: ReactNode, title: string) => {
      openInstallationReferenceImageOverlay({ content, title })
    }
  )

  const handleOpenInstallationReferenceSelectionOverlay = useEvent(
    (
      onInstallationReferencesChange: (
        nextInstallationReferences:
          | ProductPositionInstallationReference[]
          | null
      ) => void
    ) => {
      const handleInstallationReferencesChange = (
        nextInstallationReferences:
          | ProductPositionInstallationReference[]
          | null
      ) => {
        args.onInstallationReferencesChange?.(nextInstallationReferences)
        onInstallationReferencesChange(nextInstallationReferences)
      }

      openInstallationReferenceSelectionOverlay({
        productPosition: formData?.productPosition
          ? {
              variantKey: formData.productPosition.variantKey ?? undefined,
              references: cloneDeep(
                formData.productPosition.references
              ) as ProductPosition["references"]
            }
          : null,
        onSubmit: handleInstallationReferencesChange
      })
    }
  )

  const handleSubmit = useEvent((nextFormData: ProductPositionFormData) => {
    const nextProductType = getProductType(
      nextFormData.productPosition.variantKey
    )
    const nextProductTypeHasModel = nextProductType
      ? productConfigurationModelsData?.configurationModels?.models?.some(
          model => model.id === nextProductType
        )
      : false

    if (
      nextProductType &&
      nextProductTypeHasModel &&
      exists(nextFormData.isSpecialPriceGroup)
    ) {
      if (nextFormData.isSpecialPriceGroup) {
        createSpecialPriceGroup({
          projectID,
          specialPriceGroup: {
            productType: nextProductType,
            __typename: SpecialPriceGroupTypename
          }
        })
      }
      if (!nextFormData.isSpecialPriceGroup) {
        deleteSpecialPriceGroup({ productType: nextProductType, projectID })
      }
    }
    const productPositionID = productPosition?.id

    if (nextFormData.attachments && productPositionID) {
      updateAttachmentLinks({
        id: productPositionID,
        resourceType: ProductPositionTypename,
        attachmentLinks: nextFormData.attachments
      })
    }

    return onSubmit?.({
      ...nextFormData,
      productPosition: {
        ...nextFormData.productPosition,
        price: exists(nextFormData.price)
          ? nextFormData.price ?? null
          : undefined,
        priceGroup: exists(nextFormData.priceGroup)
          ? nextFormData.priceGroup ?? null
          : undefined
      }
    })
  })

  return {
    permissions,
    hasProductConfigurationModel,
    isLoadingProductConfigurationModels,
    projectOptions,
    isLoadingProjectOptions,
    roomOptions,
    isLoadingParentOptions,
    positionOptions,
    isLoadingPositionOptions,
    priceGroupOptions,
    isLoadingPriceGroupOptions,
    price,
    isLoadingPrice,
    isSpecialPriceGroup,
    isLoadingSpecialPriceGroups,
    isPriceServiceAvailable,
    installationReferences,
    isLoadingInstallationReferences,
    formRef,

    attachments: attachments?.data ?? null,
    linkedAttachments: linkedAttachments ?? null,
    attachmentCategories: attachmentCategories?.data ?? null,
    onUpload: handleUpload,

    onChange: setFormData,
    openPortfolio:
      !hasProductConfigurationModel && !hasExternalDimensioningModel
        ? handleOpenPortfolio
        : undefined,
    openProductConfiguration: hasProductConfigurationModel
      ? handleProductConfiguration
      : undefined,
    openProductDimensioning: hasExternalDimensioningModel
      ? handleProductDimensioning
      : undefined,
    openInstallationReferenceSelection:
      handleOpenInstallationReferenceSelectionOverlay,
    openInstallationReferenceImage: handleOpenInstallationReferenceImageOverlay,
    deleteInstallationReference: handleDeleteInstallationReference,
    getFilesCallback: getProductInformationFiles,
    ...args,
    defaultProjectID: effectiveDefaultProjectID,
    onSubmit: handleSubmit,
    productPosition,
    isLoadingPermissions
  }
}
